import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePreservedCallback } from '@toss/react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import './events.quiz-premium-access';
import '../features/phone-verification/tracking/events.quiz-trial';

import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';

import { getGetMyVouchersQueryOptions } from '@/api/gen/vouchers/vouchers.gen';
import { useOpenWindow } from '@/hooks';
import { useUser } from '@/lib/auth';
import { upsellingModal } from '@/shared/components/UpsellingModal';
import { getTrialVoucherContext } from '@/shared/features/trial-voucher';
import { alertDialog, confirmDialog } from '@/ui_v3';

import {
  getUsableVoucherCount,
  type PremiumAccessResult,
  type PremiumAccessState,
  type RequiredPlan,
  resolvePremiumAccess,
} from './resolvePremiumAccess';

/** 판정에 필요한 이용권만 받는다 — 이미 쓴 이용권은 조회하지 않는다. */
const UNUSED_VOUCHERS_PARAMS = { usageStatus: 'UNUSED' } as const;

const getTrialEligibility = (trialOffer?: PremiumAccessState['trialOffer']) =>
  trialOffer?.type === 'PHONE_VERIFICATION' ? 'eligible' : 'ineligible';

const getTrialTrackingType = (trialOffer?: PremiumAccessState['trialOffer']) =>
  trialOffer?.type === 'PHONE_VERIFICATION'
    ? { trial_region: 'kr' as const, trial_type: 'phone_verification' as const }
    : { trial_region: 'none' as const, trial_type: 'none' as const };

const getPremiumAccessResult = (type: PremiumAccessResult['type']) => {
  if (type === 'usable-voucher') return 'usable_voucher';
  if (type === 'phone-trial') return 'phone_trial';
  return 'pricing';
};

/**
 * 이용권·체험을 다 따진 뒤의 종착지.
 *
 * - `upselling` — 요금제 비교 모달(기본).
 * - `pricingNewTab` — 요금제 페이지를 새 탭으로. 하던 일을 끊으면 안 되는 자리(play 등).
 * - `pricingSameTab` — 요금제 페이지로 이동. 이미 이탈해도 되는 자리.
 */
export type PricingDestination =
  | 'upselling'
  | 'pricingNewTab'
  | 'pricingSameTab';

interface CommonOpenOptions {
  /** 트래킹용 진입 지점 */
  from: string;
  /** 새 typed 트래킹에서만 쓸 세부 진입 지점. 레거시 from 값은 유지한다. */
  trackingSource?: string;
  /** 현재 안내를 닫거나 취소한 뒤 호출 */
  onDismiss?: VoidFunction;
  /**
   * 판정이 끝난 시점에 아직 모달을 열어도 되는지 확인한다.
   * 조회에 왕복이 끼므로, mount 시 자동으로 여는 호출부는 그사이 언마운트됐는지 확인해야 한다.
   */
  shouldOpen?: () => boolean;
  /**
   * **이용권도 없고 무료 체험 대상도 아닐 때** 어디로 보낼지. 기본은 업셀링 모달.
   *
   * 이용권 판정을 "업셀링 모달을 여는 기능"에 묶어 두면, 요금제로 곧장 보내던 자리
   * (play 잠금 오버레이 등)는 정책을 쓰기 위해 모달까지 떠안아야 한다. 그래서 이용권·체험
   * 레이어는 훅이 계속 소유하고, **종착지만** 호출부가 고르게 한다.
   *
   * 콜백이 아니라 값인 이유 — 이동 방식을 호출부에 맡기면 흩어진다. 이미 겪었다:
   * `/pricing` 진입이 window.open·openWindow·router.push·router.replace 네 갈래로
   * 갈라져 있고, `useUpgradeToPro` 는 그걸 한 곳으로 되모으려고 만든 헬퍼다.
   * 여기서 콜백을 열어 주면 그 정리를 되돌린다. 이동과 트래킹은 훅이 계속 소유한다.
   *
   * `phone-trial` 은 여기 영향을 받지 않는다 — 인증 배너가 업셀링 모달 안에만 있어서,
   * 이걸 건너뛰면 체험 대상자가 안내를 통째로 놓친다.
   */
  pricingDestination?: PricingDestination;
  /** 플랜 관리의 미래 플랜 준비 흐름에서 업셀링 의도를 유지한다. */
  intent?: 'NEW_PURCHASE' | 'FUTURE_PURCHASE';
  startDate?: string;
}

/** 무엇이 막혔는지 아는 진입 — 이용권 등급을 판단할 수 있다. */
export interface OpenFeaturePaywallOptions extends CommonOpenOptions {
  /** 이 기능을 쓰기 위해 실제로 필요한 최소 플랜 */
  requiredPlan: RequiredPlan;
}

/** 무엇이 막혔는지 모르는 진입 — 요구 등급이라는 개념이 없다. */
export interface OpenPlanGuideOptions extends CommonOpenOptions {
  /**
   * 모바일에서 기본 선택할 가격 카드. **요구 등급이 아니다.**
   * 데스크톱은 두 카드를 모두 보여주므로 트래킹과 모바일 초기 선택에만 영향을 준다.
   */
  highlightPlan?: RequiredPlan;
}

/**
 * 유료 기능 진입 시 무엇을 보여줄지 판정하고, 그 결과에 맞는 모달을 연다.
 *
 * **판정이 끝나기 전에는 아무것도 그리지 않는다.** 모달을 먼저 띄우고 내부에서 분기하면
 * 이용권 보유자에게 가격표가 한 번 보인 뒤 교체되므로, 조회를 끝낸 뒤 한 번만 연다.
 *
 * ## 레이어 — 어디까지가 정책이고 어디부터가 호출부인가
 *
 * ```
 * usable-voucher → 이용권 안내 다이얼로그 → /profile/plan  [훅 소유] 문구·트래킹이 한 벌이어야 한다
 * phone-trial    → 업셀링 모달 + 인증 배너            [훅 소유] 배너가 그 모달 안에만 있다
 * pricing        → pricingDestination 이 고른 종착지   [호출부가 고르고, 실행은 훅]
 * ```
 *
 * 업셀링 모달을 항상 보장하지 않는다. play 잠금 오버레이처럼 요금제로 곧장 보내던 자리도
 * `pricingDestination: 'pricingNewTab'` 하나면 **자기 동작을 유지한 채** 이용권·체험
 * 판정을 앞단에 끼울 수 있다.
 *
 * 실제 기능 접근 차단은 서버 몫이다. 여기서 하는 건 "막힌 뒤 무엇을 보여줄지" 정하는
 * UX 정책이며 권한 판정을 대체하지 않는다.
 *
 * @param options.prefetchOnMount
 *   기능 페이월 CTA 가 이미 화면에 보이는 컴포넌트에서 켠다. 클릭 전에 판정 데이터를 데워
 *   대기 시간을 없앤다(트리거에 로딩 표시를 두지 않기로 했으므로 이게 유일한 장치다).
 *   숨어 있는 트리거(드롭다운 안, 조건부 노출)에서는 켜지 않는다 — 안 누를 사람 몫까지 조회하게 된다.
 */
export const usePremiumAccess = ({
  prefetchOnMount = false,
}: { prefetchOnMount?: boolean } = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['common']);
  const { isAuthenticated } = useUser();
  const router = useRouter();
  // 새 탭 이동은 로케일을 붙여야 한다. 호출부가 window.open 을 직접 부르면
  // 이 처리가 빠지는 변형이 생긴다(실제로 네 갈래로 갈라져 있었다).
  const openWindow = useOpenWindow();

  // 이 훅은 퀴즈 카드처럼 리스트로 반복 렌더되는 컴포넌트에서도 쓰인다.
  // 반환값 identity 가 매 렌더 흔들리면 소비처의 effect 가 계속 재등록되므로,
  // 콜백은 usePreservedCallback 으로 고정하고 최신 값은 호출 시점에 읽는다.
  const showUnavailableAlert = usePreservedCallback(async () => {
    await alertDialog.openAsync({
      header: { title: t('common:voucher.activateFailed.title') },
      body: t('common:voucher.commonError.body'),
    });
  });

  /**
   * 시안(Figma 28913:52177) — 여기서 활성화하지 않고 이용권 보관함으로 보낸다.
   * 활성화 확인·성공·실패 처리는 보관함이 이미 갖고 있으므로 중복해서 만들지 않는다.
   */
  const handleUsableVoucher = usePreservedCallback(
    async (
      result: Extract<PremiumAccessResult, { type: 'usable-voucher' }>,
      from: string,
      onDismiss?: VoidFunction,
    ) => {
      const { hashId } = result.voucher;
      const voucherType =
        getTrialVoucherContext(result.voucher.systemCampaignType)
          ?.voucher_type ?? 'other';

      trackEvent('quiz_voucher_use_modal_view', {
        voucher_id: hashId,
        from,
        voucher_type: voucherType,
      });
      const confirmed = await confirmDialog.openAsync({
        // 시안 28913:52177 은 320px. ResponsiveDialog 의 xs 가 mobile-lg 이상에서 320px 이라
        // 본문이 272px 폭 안에서 시안대로 두 줄로 접힌다. sm(400px) 이면 한 줄로 펴진다.
        size: 'xs',
        header: { title: t('common:voucher.available.title') },
        body: t('common:voucher.available.description'),
        cancelText: t('common:voucher.available.later'),
        confirmText: t('common:voucher.available.confirm'),
      });
      if (!confirmed) {
        onDismiss?.();
        return;
      }

      trackEvent('quiz_voucher_use_confirm_click', {
        voucher_id: hashId,
        from,
        voucher_type: voucherType,
      });
      // /voucher 는 #3359 에서 삭제되고 next.config 307 리다이렉트만 남았다. 클라 라우터가
      // 못 찾는 경로라 push 하면 풀 리로드로 떨어지므로 실제 화면으로 바로 보낸다.
      await router.push('/profile/plan');
    },
  );

  /** 판정 결과를 화면에 반영한다. 두 진입이 공유하는 마지막 단계. */
  const present = usePreservedCallback(
    async (
      result: PremiumAccessResult,
      {
        from,
        trackingSource = from,
        onDismiss,
        pricingDestination = 'upselling',
        intent,
        startDate,
      }: CommonOpenOptions,
      initialPlanType: RequiredPlan,
    ) => {
      const openUpselling = (showPhoneTrialBanner: boolean) => {
        const intentOptions =
          intent === 'FUTURE_PURCHASE' && startDate
            ? { intent, startDate }
            : { intent: 'NEW_PURCHASE' as const };

        upsellingModal.open({
          initialPlanType,
          from,
          onClose: onDismiss,
          showPhoneTrialBanner,
          ...intentOptions,
        });
      };

      switch (result.type) {
        case 'usable-voucher':
          await handleUsableVoucher(result, from, onDismiss);
          return;
        case 'phone-trial':
          openUpselling(true);
          return;
        case 'pricing':
          // 이용권·체험을 먼저 다 따져 본 뒤의 종착지. 여기서만 호출부가 고를 수 있다.
          if (pricingDestination === 'pricingNewTab') {
            trackEvent('quiz_premium_access_pricing_navigate', {
              feature_context: trackingSource,
              required_plan: initialPlanType,
              destination: 'pricing_new_tab',
            });
            openWindow('/pricing');
            return;
          }
          if (pricingDestination === 'pricingSameTab') {
            trackEvent('quiz_premium_access_pricing_navigate', {
              feature_context: trackingSource,
              required_plan: initialPlanType,
              destination: 'pricing_same_tab',
            });
            await router.push('/pricing');
            return;
          }
          openUpselling(false);
          return;
      }
    },
  );

  /**
   * A. 기능 페이월 — 무엇이 막혔는지 아는 진입.
   * 요구 등급이 실제 값이므로 이용권 등급을 판단할 수 있다.
   */
  const openFeaturePaywall = usePreservedCallback(
    async (options: OpenFeaturePaywallOptions) => {
      const { requiredPlan, from, trackingSource = from, shouldOpen } = options;

      // 비로그인 사용자는 조회할 상태가 없다. 바로 가격 안내로 보낸다.
      if (!isAuthenticated) {
        trackEvent('quiz_premium_access_result_view', {
          feature_context: trackingSource,
          required_plan: requiredPlan,
          result: 'pricing',
          usable_voucher_count: 0,
          trial_eligibility: 'not_evaluated',
          trial_region: 'none',
          trial_type: 'none',
        });
        await present({ type: 'pricing', requiredPlan }, options, requiredPlan);
        return;
      }

      let state: PremiumAccessState;
      try {
        // 판정에 쓰이는 값은 캐시가 아니라 최신 상태여야 한다. 이용권을 방금 쓴 사용자에게
        // 다시 이용권을 권하거나, 방금 인증한 사용자에게 인증을 또 권하면 안 된다.
        const vouchersResponse = await queryClient.fetchQuery({
          ...getGetMyVouchersQueryOptions(UNUSED_VOUCHERS_PARAMS),
          staleTime: 0,
        });

        state = {
          requiredPlan,
          trialOffer: vouchersResponse.data.trialOffer,
          vouchers: vouchersResponse.data.items,
          canUseVouchers: vouchersResponse.data.canUseVouchers,
        };
      } catch (error) {
        // 조회에 실패했다고 가격 안내로 넘기면, 이용권 보유자에게 결제를 권하는
        // 바로 그 문제가 네트워크 장애 때마다 되살아난다. 폴백하지 않는다.
        logger.captureError(error, {
          tags: { feature: 'premium_access', action: 'resolve' },
          extra: { from, requiredPlan },
        });
        await showUnavailableAlert();
        return;
      }

      // 조회 왕복 사이에 화면을 벗어났다면 이제 와서 띄우지 않는다.
      if (shouldOpen && !shouldOpen()) {
        return;
      }

      const result = resolvePremiumAccess(state);
      trackEvent('quiz_premium_access_result_view', {
        feature_context: trackingSource,
        required_plan: requiredPlan,
        result: getPremiumAccessResult(result.type),
        usable_voucher_count: getUsableVoucherCount(state),
        trial_eligibility: getTrialEligibility(state.trialOffer),
        ...getTrialTrackingType(state.trialOffer),
      });
      await present(result, options, requiredPlan);
    },
  );

  /**
   * B. 플랜 안내 — 무엇이 막혔는지 모르는 진입(헤더 칩, 사이드바 업그레이드 등).
   *
   * 요구 등급을 모르므로 보유 이용권은 매칭하지 않는다. 다만 서버가 최종 판정한
   * 체험 제안은 기능 등급과 무관하므로 같은 `/vouchers/my` 응답에서 확인한다.
   */
  const openPlanGuide = usePreservedCallback(
    async (options: OpenPlanGuideOptions) => {
      const {
        from,
        trackingSource = from,
        shouldOpen,
        highlightPlan = 'PRO',
      } = options;

      if (!isAuthenticated) {
        trackEvent('quiz_premium_access_result_view', {
          feature_context: trackingSource,
          required_plan: highlightPlan,
          result: 'pricing',
          usable_voucher_count: 0,
          trial_eligibility: 'not_evaluated',
          trial_region: 'none',
          trial_type: 'none',
        });
        await present(
          { type: 'pricing', requiredPlan: highlightPlan },
          options,
          highlightPlan,
        );
        return;
      }

      let trialOffer: PremiumAccessState['trialOffer'];
      try {
        const vouchersResponse = await queryClient.fetchQuery({
          ...getGetMyVouchersQueryOptions(UNUSED_VOUCHERS_PARAMS),
          staleTime: 0,
        });
        trialOffer = vouchersResponse.data.trialOffer;
      } catch (error) {
        logger.captureError(error, {
          tags: { feature: 'premium_access', action: 'plan_guide' },
          extra: { from, highlightPlan },
        });
        await showUnavailableAlert();
        return;
      }

      if (shouldOpen && !shouldOpen()) {
        return;
      }

      // 기능 요구 등급을 모르는 플랜 안내에서는 이용권을 매칭하지 않는다.
      const state = {
        requiredPlan: highlightPlan,
        trialOffer,
        vouchers: [],
        canUseVouchers: false,
      };
      const result = resolvePremiumAccess(state);
      trackEvent('quiz_premium_access_result_view', {
        feature_context: trackingSource,
        required_plan: highlightPlan,
        result: getPremiumAccessResult(result.type),
        usable_voucher_count: getUsableVoucherCount(state),
        trial_eligibility: getTrialEligibility(trialOffer),
        ...getTrialTrackingType(trialOffer),
      });

      await present(result, options, highlightPlan);
    },
  );

  /**
   * 기능 페이월 CTA 가 화면에 보일 때 미리 호출해 둔다.
   * 클릭 시점의 대기 시간을 없애는 유일한 장치다 — 트리거에 로딩 표시를 두지 않기 때문.
   *
   * 플랜 안내도 같은 응답의 체험 제안을 사용하므로 이 쿼리 하나만 데운다.
   */
  const prefetch = usePreservedCallback(() => {
    if (!isAuthenticated) {
      return;
    }
    void queryClient.prefetchQuery(
      getGetMyVouchersQueryOptions(UNUSED_VOUCHERS_PARAMS),
    );
  });

  useEffect(() => {
    if (prefetchOnMount) {
      prefetch();
    }
  }, [prefetchOnMount, prefetch]);

  return useMemo(
    () => ({ openFeaturePaywall, openPlanGuide, prefetch }),
    [openFeaturePaywall, openPlanGuide, prefetch],
  );
};
